import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormControl, AbstractControl, Validator } from '@angular/forms';
import { fromEvent, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs/operators';
import * as moment from 'moment';

import {
  ValueAccessorBase,
  ValueAccessorProvider,
  NgValidatorsProvider
} from '@sd-portal/shared/value-accessor';
import { ErrorStateMatcher } from '@angular/material/core';

@Component({
  selector: 'sd-time-input',
  templateUrl: './time-input.component.html',
  styleUrls: ['./time-input.component.scss'],
  providers: [
    ValueAccessorProvider.provider(TimeInputComponent),
    NgValidatorsProvider.provider(TimeInputComponent)
  ]
})
export class TimeInputComponent
  extends ValueAccessorBase<moment.Moment | undefined>
  implements Validator, OnInit, OnChanges
{
  @Input() automatedDataValue: moment.Moment | null;
  @Input() disableAutoFill: boolean = false;
  @Input() baseDate: moment.Moment;
  @Input() matLabel: string;
  @Input() matPlaceholder: string = '--:--';
  @Input() matFloatLabel: 'auto' | 'always' | 'never' = 'auto';
  @Input() required: boolean;
  @Input() leftHintType: string;
  @Input() leftHint: [];
  @Input() rightHintType: string;
  @Input() rightHint: [];

  @Output() timeSet = new EventEmitter<moment.Moment | undefined>();
  @Output() resetAutomation = new EventEmitter();
  @ViewChild('strInput', { static: true }) strInput: ElementRef;

  /* component properties */
  strControl: FormControl = new FormControl();
  hasAutomatedData: boolean = false;
  externalControl: AbstractControl;
  isErrorState: ErrorStateMatcher = {
    isErrorState: (_control: FormControl) => {
      const { externalControl } = this;
      if (externalControl) {
        const { touched, dirty, invalid } = externalControl;
        return (touched || dirty) && invalid;
      }
      return false;
    }
  };

  get isAutomatedValue(): boolean {
    if (this.hasAutomatedData && this.value && this.automatedDataValue) {
      const inputValue = this.value.clone().set({ second: 0, millisecond: 0 });
      const automationValue = this.automatedDataValue.clone().set({ second: 0, millisecond: 0 });

      return inputValue.isSame(automationValue);
    }

    return false;
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  private blur$: Observable<any>;
  private keypress$: Observable<any>;
  private focus$: Observable<any>;
  /* eslint-enable @typescript-eslint/no-explicit-any */

  private baseDateStr: string;

  constructor(private readonly elementRef: ElementRef) {
    super();
  }

  /* life-cycle */
  ngOnInit() {
    this.initFocusBlurObservables();
    this.checkAutomatedData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.baseDate) {
      const d = changes.baseDate.currentValue;
      if (d && d.isValid()) {
        const lastBaseDateStr = this.baseDateStr;
        this.baseDateStr = d.format('MM/DD/YYYY');
        if (this.baseDateStr !== lastBaseDateStr && this.strControl.value) {
          this.updateTimeOnDate(this.strControl.value, true);
        }
      }
    }
  }

  checkAutomatedData() {
    if (this.automatedDataValue) {
      this.hasAutomatedData = true;
    }
  }

  resetAutomatedValue() {
    this.setControlValue(this.automatedDataValue || undefined, true, true);
    this.resetAutomation.emit();
  }

  /* value accessor overrides */
  writeValue(value: moment.Moment) {
    super.writeValue(value);
    this.getTimeFromDate().then(strTime => {
      this.strControl.setValue(strTime, { emitEvent: true });
    });
  }

  /**
   * Validator interface
   */
  validate(c: AbstractControl) {
    this.externalControl = c;
    return null;
  }
  registerOnValidatorChange(_fn: () => void) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trigger(event: any) {
    this.triggerTouch(event);
  }

  /* privates */
  private initFocusBlurObservables() {
    // obs to init value main valueChange event on element focus
    this.focus$ = fromEvent(this.strInput.nativeElement, 'focus');

    // obs to fun time calc/validation on blur
    this.blur$ = fromEvent(this.strInput.nativeElement, 'blur').pipe(
      map(() => {
        this.updateTimeOnDate(this.strControl.value);
      })
    );

    // obs for running time calc/validation on [enter] press
    this.keypress$ = fromEvent(this.elementRef.nativeElement, 'keypress').pipe(
      map((e: KeyboardEvent) => e),
      this.onEnterKeypress(),
      tap(() => {
        this.updateTimeOnDate(this.strControl.value, true);
        this.initInputSubscription();
      })
    );

    // sub to focus event:obs
    this.focus$.subscribe(() => {
      this.initInputSubscription();
    });
  }

  private initInputSubscription() {
    this.strControl.valueChanges
      .pipe(
        debounceTime(1500),
        takeUntil(this.keypress$),
        takeUntil(this.blur$),
        distinctUntilChanged()
      )
      .subscribe(val => this.updateTimeOnDate(val, true));
  }

  private readonly onEnterKeypress = () =>
    filter((key: KeyboardEvent) => {
      return key.code === 'Enter' || key.code === 'NumpadEnter' ? true : false;
    });

  // datetime conversions
  private getTimeFromDate(): Promise<string> {
    return new Promise(resolve => {
      let strTime: string | undefined;

      if (this.value && this.value instanceof moment) {
        strTime = this.value.format('HH:mm');
      } else if (this.value && typeof this.value === 'string') {
        //HACK: on value load from server, need to parse string value
        this.value = moment.utc(this.value);
        strTime = this.value.format('HH:mm');
      } else if (this.value === undefined) {
        strTime = '';
      }
      if (strTime) {
        resolve(strTime);
      }
    });
  }
  private updateTimeOnDate(time: string, dataChanged: boolean = false) {
    if (time) {
      let d: moment.Moment;
      if (this.baseDateStr) {
        d = moment.utc(`${this.baseDateStr} ${time}`, 'MM/DD/YYYY H:mm');
      } else if (this.value) {
        const rootDate = this.value.format('MM/DD/YYYY');
        this.baseDateStr = rootDate;
        d = moment.utc(`${this.baseDateStr} ${time}`, 'MM/DD/YYYY H:mm');
      } else {
        d = moment(time, 'H:mm');
      }

      const prevTime = this.value ? this.value.format('HH:mm') : '';
      const setTime = d.format('HH:mm');
      const timeValueChanged = prevTime !== setTime;

      if (d.isValid()) {
        if (timeValueChanged) {
          let updateDate: moment.Moment;
          if (!this.value && this.baseDate) {
            updateDate = moment.utc(this.baseDate);
          } else {
            updateDate = moment.utc(this.value);
          }
          updateDate.set({
            hours: d.hours(),
            minute: d.minutes(),
            seconds: 0,
            milliseconds: 0
          });

          this.setControlValue(updateDate, false, timeValueChanged);
        } else {
          this.setControlValue(d, false, dataChanged);
        }
      }
    } else {
      this.setControlValue(undefined, false, (this.value ?? '') !== '');
    }
  }

  private setControlValue(
    date?: moment.Moment,
    reset: boolean = false,
    dataChanged: boolean = false
  ) {
    if (dataChanged) {
      this.value = date;
      this.changed.forEach(f => f(date));
      this.timeSet.emit(this.value);
    }

    if (!this.disableAutoFill || reset) {
      this.getTimeFromDate().then(strTime => {
        this.strControl.setValue(strTime, { emitEvent: false });
      });
    }
  }

  setDisabledState(isDisabled: boolean) {
    if (this.strControl) {
      isDisabled ? this.strControl.disable() : this.strControl.enable();
    }
  }
}
